import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Art } from '../components/Art';
import { Stage } from '../components/Stage';
import { strip_map } from '../art/art';
import { MapMarker, useRouteGlide } from '../components/MapView';
import type { LatLng } from '../lib/types';
import { VehicleArt } from '../art/Vehicle';
import type { Line } from '../lib/polyline';
import type { VehicleType } from '../lib/types';
import { bearing, turn } from '../lib/eta';
import { haversine } from '../lib/geo';

/** Pickup ring (route start) as the mockup draws it. */
export const StartRing = ({ at, color = 'var(--acText)', size = 18, stroke = 4 }: { at: LatLng; color?: string; size?: number; stroke?: number }) => (
  <MapMarker at={at} z={3}>
    <span style={{ display: 'block', width: size, height: size, borderRadius: size / 2, boxSizing: 'border-box', border: `${stroke}px solid ${color}`, background: 'var(--bg)' }} />
  </MapMarker>
);

/** Drop square. */
export const DropSquare = ({ at, size = 14 }: { at: LatLng; size?: number }) => (
  <MapMarker at={at} z={3}>
    <span style={{ display: 'block', width: size, height: size, borderRadius: 3, background: 'var(--ink)', boxShadow: '0 0 0 2.5px var(--bg)' }} />
  </MapMarker>
);

/** A label pill beside a map point; side = which side of the point it sits on. */
export function MapPill({ at, side = 'left', children, gap = 14 }: { at: LatLng; side?: 'left' | 'right' | 'top'; children: ReactNode; gap?: number }) {
  const anchor = side === 'left' ? 'right' : side === 'right' ? 'left' : 'bottom';
  return (
    <MapMarker at={at} anchor={anchor} z={4}>
      <div
        style={{
          margin: side === 'left' ? `0 ${gap}px 0 0` : side === 'right' ? `0 0 0 ${gap}px` : `0 0 ${gap}px 0`,
          height: 28,
          padding: '0 10px',
          borderRadius: 14,
          background: 'var(--float)',
          boxShadow: 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 13,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          color: 'var(--ink)',
        }}
      >
        {children}
      </div>
    </MapMarker>
  );
}

/**
 * Which way the vehicle is going (degrees, 0 = north), turning the short way round: the bearing of its last real
 * move (over 12 m), else the heading the driver's phone sent. Null until either is known.
 */
function useHeading(at: LatLng, sent: number | null | undefined): number | null {
  const prev = useRef(at);
  const [deg, setDeg] = useState<number | null>(sent ?? null);
  useEffect(() => {
    const from = prev.current;
    let target: number | null = null;
    if (haversine(from, at) > 12) target = bearing(from, at);
    else if (sent != null && deg == null) target = sent;
    if (haversine(from, at) > 12) prev.current = at;
    if (target == null) return;
    setDeg((d) => (d == null ? target : d + turn(((d % 360) + 360) % 360, target)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at.lat, at.lng, sent]);
  return deg;
}

/**
 * Vehicle disc on the map (driver, nearby drivers). It glides to each new position (`glide` ms) — along the road
 * line when one is given (`route`), so it follows the turns — and, with `heading`, turns to the way it is going:
 * an aqua nose on its ring points there and the drawing faces left or right with it.
 */
export function VehicleDisc({ at, type, size = 42, ring = 2.5, halo, faded, glide = 1600, route = null, heading }: { at: LatLng; type: VehicleType; size?: number; ring?: number; halo?: boolean; faded?: boolean; glide?: number; route?: Line | null; heading?: number | null }) {
  const w = Math.round(size * 0.76);
  const pos = useRouteGlide(at, route, glide);
  const deg = useHeading(at, heading);
  const west = deg != null && ((deg % 360) + 360) % 360 > 180;
  return (
    <MapMarker at={pos} z={faded ? 1 : 5}>
      <span
        data-heading={deg != null ? Math.round(((deg % 360) + 360) % 360) : undefined}
        style={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: size / 2,
          background: 'var(--float)',
          boxShadow: ring ? `0 0 0 ${ring}px var(--ac)${halo ? ', 0 0 0 9px var(--acSoft)' : ''}, var(--shadow)` : 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: faded ? 0.55 : 1,
          transition: 'opacity .3s, box-shadow .3s',
          animation: 'zr-popin .3s var(--ease-out) both',
        }}
      >
        {heading !== undefined && deg != null && (
          <span aria-hidden="true" style={{ position: 'absolute', inset: -9, transform: `rotate(${deg}deg)`, transition: 'transform .9s var(--ease-out)', pointerEvents: 'none' }}>
            <span style={{ position: 'absolute', left: '50%', top: 0, marginLeft: -5, width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '7px solid var(--acText)' }} />
          </span>
        )}
        <VehicleArt type={type} width={w} shadow={false} style={{ transform: west ? 'scaleX(-1)' : undefined, transition: 'transform .4s var(--ease-out)' }} />
      </span>
    </MapMarker>
  );
}

/** "You" dot: filled primary with a ring of the background colour. */
export const YouDot = ({ at, size = 18 }: { at: LatLng; size?: number }) => (
  <MapMarker at={at} z={6}>
    <span style={{ display: 'block', width: size, height: size, borderRadius: size / 2, background: 'var(--pri)', boxShadow: '0 0 0 4px var(--bg)' }} />
  </MapMarker>
);

/** The strip of hills the mockups draw along the top edge of a map sheet. */
export const MapStrip = () => (
  <Stage bottom={0} height={80}>
    <Art piece={strip_map} />
  </Stage>
);
